import React, { useEffect, useState } from 'react';
import './App.css';
import Home from './MVVM/View/components/Home.js';
import About from './MVVM/View/components/About.js';
import Contact from './MVVM/View/components/Contact.js';
import FAQ from './MVVM/View/components/FAQ.js';
import Gdpr from './MVVM/View/components/Gdpr.js';
import AdminLanding from './MVVM/View/components/AdminLanding.js';
import ServiceDetail from './MVVM/View/components/ServiceDetail.js';
import { warmupInitialData } from './MVVM/ViewModel/AppViewModel.js';
import { supabase } from './MVVM/Model/Services/serviceService.js';

/*
 * App is the composition root for the SPA:
 * - client-side route switching
 * - admin auth/session flow
 * - global SEO metadata per route
 * Keep cross-page concerns here and keep page-specific data in ViewModel/Model.
 */
const ADMIN_SESSION_MS = 60 * 60 * 1000;
const ADMIN_SESSION_EXPIRY_KEY = 'adminSessionExpiry';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://shekonomi.com').replace(/\/+$/, '');
const DEFAULT_OG_IMAGE = `${SITE_URL}/Images/works.jpg`;

const buildSeoMeta = (path) => {
  const byRoute = {
    '/': {
      title: 'Redovisningsbyrå i Sverige - Bokföring, Skatt och Rådgivning',
      description: 'Auktoriserad redovisningsbyrå med bokföring, deklaration, skatt och ekonomisk rådgivning för företag i hela Sverige.',
      canonicalPath: '/',
      robots: 'index,follow',
    },
    '/omoss': {
      title: 'Om oss - Redovisningsbyrån',
      description: 'Lär känna vårt arbetssätt, vår erfarenhet och hur vi hjälper företag med trygg redovisning och rådgivning.',
      canonicalPath: '/omoss',
      robots: 'index,follow',
    },
    '/kontakt': {
      title: 'Kontakt - Boka konsultation',
      description: 'Kontakta oss för bokföring, deklaration och ekonomisk rådgivning. Vi återkopplar snabbt på helgfria vardagar.',
      canonicalPath: '/kontakt',
      robots: 'index,follow',
    },
    '/faq': {
      title: 'FAQ - Vanliga frågor och svar',
      description: 'Vanliga frågor om bokföring, deklaration och samarbete med oss som redovisningspartner.',
      canonicalPath: '/faq',
      robots: 'index,follow',
    },
    '/gdpr': {
      title: 'GDPR och personuppgifter',
      description: 'Läs hur vi hanterar personuppgifter enligt GDPR och vilka rättigheter du har.',
      canonicalPath: '/gdpr',
      robots: 'index,follow',
    },
  };

  const serviceDetailMatch = path.match(/^\/tjanster\/([^/]+)\/?$/);
  if (serviceDetailMatch) {
    return {
      title: 'Tjänst - Redovisningsbyrån',
      description: 'Utforska tjänsten och boka kontakt med vårt team för nästa steg.',
      canonicalPath: `/tjanster/${serviceDetailMatch[1]}`,
      robots: 'index,follow',
    };
  }

  if (path.startsWith('/admin')) {
    return {
      title: 'Admin - Redovisningsbyrån',
      description: 'Adminpanel.',
      canonicalPath: '/admin',
      robots: 'noindex,nofollow',
    };
  }

  return byRoute[path] || byRoute['/'];
};

const upsertMetaTag = (attributeName, attributeValue, content) => {
  if (typeof document === 'undefined') return;
  let element = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertCanonicalLink = (href) => {
  if (typeof document === 'undefined') return;
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const upsertJsonLd = (data) => {
  if (typeof document === 'undefined') return;
  const id = 'seo-jsonld';
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
};

const getStoredAdminExpiry = () => {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_EXPIRY_KEY);
    const num = raw ? Number(raw) : null;
    if (!num || Number.isNaN(num)) return null;
    return num;
  } catch (_) {
    return null;
  }
};

const setStoredAdminExpiry = () => {
  const expiry = Date.now() + ADMIN_SESSION_MS;
  try {
    localStorage.setItem(ADMIN_SESSION_EXPIRY_KEY, String(expiry));
  } catch (_) {
    // ignore storage errors
  }
  return expiry;
};

const clearStoredAdminExpiry = () => {
  try {
    localStorage.removeItem(ADMIN_SESSION_EXPIRY_KEY);
  } catch (_) {
    // ignore storage errors
  }
};

const clearSupabaseAuthStorage = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('sb-'))
      .forEach((key) => localStorage.removeItem(key));
  } catch (_) {
    // ignore storage errors
  }

  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('sb-'))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (_) {
    // ignore storage errors
  }
};

function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [sessionRemainingMs, setSessionRemainingMs] = useState(null);
  const isAdminRoute = path.startsWith('/admin');
  const isAdminContentRoute = path === '/admin/home' || path === '/admin/omoss' || path === '/admin/aboutus' || path === '/admin/faq';
  const showAdminBanner = isAdminRoute && isAdminAuthenticated && sessionRemainingMs !== null;

  useEffect(() => {
    const syncPath = () => {
      setPath(window.location.pathname);
    };

    window.addEventListener('popstate', syncPath);
    return () => window.removeEventListener('popstate', syncPath);
  }, []);

  useEffect(() => {
    const seo = buildSeoMeta(path);
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath}`;

    document.title = seo.title;
    upsertMetaTag('name', 'description', seo.description);
    upsertMetaTag('name', 'robots', seo.robots);
    upsertMetaTag('property', 'og:type', 'website');
    upsertMetaTag('property', 'og:title', seo.title);
    upsertMetaTag('property', 'og:description', seo.description);
    upsertMetaTag('property', 'og:url', canonicalUrl);
    upsertMetaTag('property', 'og:image', DEFAULT_OG_IMAGE);
    upsertMetaTag('name', 'twitter:card', 'summary_large_image');
    upsertMetaTag('name', 'twitter:title', seo.title);
    upsertMetaTag('name', 'twitter:description', seo.description);
    upsertMetaTag('name', 'twitter:image', DEFAULT_OG_IMAGE);
    upsertCanonicalLink(canonicalUrl);

    upsertJsonLd({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: 'Redovisningsbyrån',
          url: SITE_URL,
        },
        {
          '@type': 'AccountingService',
          name: 'Redovisningsbyrån',
          url: SITE_URL,
          image: DEFAULT_OG_IMAGE,
          areaServed: 'SE',
          serviceType: ['Bokföring', 'Deklaration', 'Skatt', 'Ekonomisk rådgivning'],
        },
      ],
    });
  }, [path]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    warmupInitialData();
  }, []);

  useEffect(() => {
    const className = 'admin-has-banner';
    if (showAdminBanner) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }

    return () => {
      document.body.classList.remove(className);
    };
  }, [showAdminBanner]);

  useEffect(() => {
    let active = true;
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      if (!active) return;
      const isAdmin = Boolean(session?.user?.app_metadata?.admin);
      setIsAdminAuthenticated(isAdmin);
      if (isAdmin) {
        const storedExpiry = getStoredAdminExpiry();
        const expiry = storedExpiry && storedExpiry > Date.now() ? storedExpiry : setStoredAdminExpiry();
        setSessionRemainingMs(Math.max(0, expiry - Date.now()));
      }
    };
    init();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      const isAdmin = Boolean(session?.user?.app_metadata?.admin);
      setIsAdminAuthenticated(isAdmin);
      if (isAdmin) {
        const storedExpiry = getStoredAdminExpiry();
        const expiry = storedExpiry && storedExpiry > Date.now() ? storedExpiry : setStoredAdminExpiry();
        setSessionRemainingMs(Math.max(0, expiry - Date.now()));
      }
      if (!session) {
        setLoginPassword('');
        setLoginError('');
        setSessionRemainingMs(null);
      }
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        setResetMessage('Välj ett nytt lösenord.');
      }
    });

    return () => {
      active = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, []);

  const handleAdminLogout = async () => {
    try {
      // Clear both local and global sessions to avoid immediate re-login from cached session
      await supabase.auth.signOut({ scope: 'local' });
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      // Best-effort logout; still clear local state
      // eslint-disable-next-line no-console
      console.error('Logout failed', err);
    }
    // Double-check session cleared; if still present, clear local scope again
    const { data: sessionCheck } = await supabase.auth.getSession();
    if (sessionCheck?.session) {
      await supabase.auth.signOut({ scope: 'local' });
    }

    // Drop any persisted Supabase auth tokens to prevent silent re-login
    clearSupabaseAuthStorage();
    clearStoredAdminExpiry();

    setIsAdminAuthenticated(false);
    setLoginPassword('');
    setLoginError('');
    setSessionRemainingMs(null);
    setAuthMessage('Du har loggat ut.');
    window.location.replace('/admin');
  };

  const formatMsToHms = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds]
      .map((v) => v.toString().padStart(2, '0'))
      .join(':');
  };

  const handlePasswordResetRequest = async () => {
    setLoginError('');
    setAuthMessage('');
    if (!loginEmail.trim()) {
      setLoginError('Ange din e-post för återställning.');
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(loginEmail.trim(), {
      redirectTo: `${window.location.origin}/admin`,
    });
    if (error) {
      setLoginError(error.message || 'Kunde inte skicka återställningslänk.');
      return;
    }
    setAuthMessage('Återställningslänk skickad om e-postadressen är registrerad.');
  };

  const renderSessionBanner = () => {
    if (!showAdminBanner) return null;
    return (
      <div className="admin-session-banner">
        <p>
          Av säkerhetsskäl loggas du ut efter 1 timme. Tid kvar: {formatMsToHms(sessionRemainingMs)}
        </p>
      </div>
    );
  };

  const handlePasswordRecoverySubmit = async (e) => {
    e.preventDefault();
    setResetMessage('');
    if (!newPassword.trim()) {
      setResetMessage('Ange ett nytt lösenord.');
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword.trim() });
    if (error) {
      setResetMessage(error.message || 'Kunde inte uppdatera lösenord.');
      return;
    }
    setResetMessage('Lösenord uppdaterat. Logga in igen.');
    setIsPasswordRecovery(false);
    setNewPassword('');
    await supabase.auth.signOut();
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setAuthMessage('');

    const { data, error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });

    if (error) {
      setLoginError(error.message || 'Fel e-post eller lösenord.');
      return;
    }

    const session = data?.session;
    const isAdmin = Boolean(session?.user?.app_metadata?.admin);

    if (!isAdmin) {
      await supabase.auth.signOut();
      setLoginError('Inte behörig admin.');
      return;
    }

    setIsAdminAuthenticated(true);
    setLoginPassword('');
    setAuthMessage('Admin inloggad.');
    const expiry = setStoredAdminExpiry();
    setSessionRemainingMs(Math.max(0, expiry - Date.now()));
    window.location.replace('/admin');
  };

  useEffect(() => {
    let logoutTimer;
    let countdownInterval;

    const clearTimers = () => {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };

    if (!isAdminAuthenticated) {
      clearTimers();
      setSessionRemainingMs(null);
      return () => {};
    }

    const storedExpiry = getStoredAdminExpiry();
    const expiry = storedExpiry && storedExpiry > Date.now() ? storedExpiry : setStoredAdminExpiry();
    const initialRemaining = Math.max(0, expiry - Date.now());
    setSessionRemainingMs(initialRemaining);

    countdownInterval = setInterval(() => {
      setSessionRemainingMs((prev) => {
        if (prev === null) return null;
        return Math.max(0, prev - 1000);
      });
    }, 1000);

    logoutTimer = setTimeout(async () => {
      await supabase.auth.signOut();
      // Ensure any cached Supabase auth state is removed so the next login is a clean start.
      clearSupabaseAuthStorage();
      clearStoredAdminExpiry();
      setIsAdminAuthenticated(false);
      setSessionRemainingMs(null);
      setAuthMessage('Sessionen har gått ut. Logga in igen.');
      window.location.href = '/admin';
    }, initialRemaining || ADMIN_SESSION_MS);

    return () => {
      clearTimers();
    };
  }, [isAdminAuthenticated]);

  const renderAdminLogin = () => (
    <div className="App admin-blocked-screen">
      <div className="admin-blocked-card admin-login-card">
        <h1>Admin inloggning</h1>
        <p>Logga in för att redigera admininnehåll.</p>
        <form className="admin-login-form" onSubmit={handleAdminLogin}>
          <label>
            E-post
            <input
              type="email"
              value={loginEmail}
              onChange={(event) => setLoginEmail(event.target.value)}
              placeholder="admin@test.com"
              autoComplete="username"
              required
            />
          </label>
          <label>
            Lösenord
            <input
              type="password"
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
              placeholder="••••"
              autoComplete="current-password"
              required
            />
          </label>
          <div className="admin-login-actions">
            <button className="admin-blocked-button" type="submit">Logga in</button>
            <button
              className="admin-cancel-button"
              type="button"
              onClick={handlePasswordResetRequest}
            >
              Glömt lösenord
            </button>
            <button
              className="admin-cancel-button"
              type="button"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Avbryt
            </button>
          </div>
        </form>
        {loginError && <p className="admin-login-error">{loginError}</p>}
        {authMessage && <p className="admin-login-info">{authMessage}</p>}
      </div>
    </div>
  );

  if (isPasswordRecovery) {
    return (
      <div className="App admin-blocked-screen">
        <div className="admin-blocked-card admin-login-card">
          <h1>Återställ lösenord</h1>
          <p>Välj ett nytt lösenord för ditt konto.</p>
          <form className="admin-login-form" onSubmit={handlePasswordRecoverySubmit}>
            <label>
              Nytt lösenord
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
            </label>
            <div className="admin-login-actions">
              <button className="admin-blocked-button" type="submit">Spara nytt lösen</button>
            </div>
          </form>
          {resetMessage && <p className="admin-login-info">{resetMessage}</p>}
        </div>
      </div>
    );
  }

  if (isAdminRoute && !isDesktop) {
    return (
      <div className="App admin-blocked-screen">
        <div className="admin-blocked-card">
          <h1>Admin är endast för större skärmar</h1>
          <p>Öppna adminpanelen på en dator eller större surfplatta.</p>
          <button
            className="admin-blocked-button"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Till startsidan
          </button>
        </div>
      </div>
    );
  }

  if (isAdminRoute && !isAdminAuthenticated) {
    return renderAdminLogin();
  }

  if (path === '/admin') {
    return (
      <div className="App">
        {renderSessionBanner()}
        <AdminLanding onLogout={handleAdminLogout} />
      </div>
    );
  }

  if (path === '/admin/home' && isAdminAuthenticated) {
    return (
      <div className="App">
        {renderSessionBanner()}
        <Home adminMode onAdminLogout={handleAdminLogout} isAdminLoggedIn={isAdminAuthenticated} />
      </div>
    );
  }

  if ((path === '/admin/omoss' || path === '/admin/aboutus') && isAdminAuthenticated) {
    return (
      <div className="App">
        {renderSessionBanner()}
        <About adminMode onAdminLogout={handleAdminLogout} isAdminLoggedIn={isAdminAuthenticated} />
      </div>
    );
  }

  if (path === '/admin/faq' && isAdminAuthenticated) {
    return (
      <div className="App">
        {renderSessionBanner()}
        <FAQ adminMode onAdminLogout={handleAdminLogout} isAdminLoggedIn={isAdminAuthenticated} />
      </div>
    );
  }

  if (isAdminContentRoute && !isAdminAuthenticated) {
    return renderAdminLogin();
  }

  const serviceDetailMatch = path.match(/^\/tjanster\/([^/]+)\/?$/);
  if (serviceDetailMatch) {
    const serviceId = serviceDetailMatch[1];
    return (
      <div className="App">
        <ServiceDetail serviceId={serviceId} />
      </div>
    );
  }

  if (path === '/omoss') {
    return (
      <div className="App">
        <About />
      </div>
    );
  }

  if (path === '/faq') {
    return (
      <div className="App">
        <FAQ />
      </div>
    );
  }

  if (path === '/kontakt') {
    return (
      <div className="App">
        <Contact />
      </div>
    );
  }

  if (path === '/gdpr') {
    return (
      <div className="App">
        <Gdpr />
      </div>
    );
  }

  return (
    <div className="App">
      <Home />
    </div>
  );
}

export default App;
